/**
 * Single Brand Page Functionality
 * Handles Smooth Scroll and Table of Contents (TOC) State
 */

document.addEventListener('DOMContentLoaded', () => {
  'use strict';

  // TOC Elements
  const tocLinks = document.querySelectorAll('.bfwt-brand-toc__link');
  const HASH_ALIASES = {
    '#expert-tip': '#ally-summary',
    '#intel-summary': '#ally-summary',
    '#ally-intelligence': '#ally-summary',
    '#deals': '#deals-discounts',
    '#related': '#alternative-brands',
    '#review': '#brand-review',
  };

  const normalizeHash = (rawHash) => {
    if (!rawHash) return '';
    const hash = rawHash.startsWith('#') ? rawHash : '#' + rawHash;
    return HASH_ALIASES[hash] || hash;
  };

  const getHashTarget = (rawHash) => {
    const normalized = normalizeHash(rawHash);
    if (!normalized) {
      return null;
    }

    const id = normalized.substring(1);
    if (!id) {
      return null;
    }

    return document.getElementById(id);
  };

  const tocHashes = new Set(
    Array.from(tocLinks)
      .map((link) => normalizeHash(link.getAttribute('href') || ''))
      .filter(Boolean)
  );

  const isTocHash = (rawHash) => tocHashes.has(normalizeHash(rawHash));

  // Read --header-stack live so a single source of truth (CSS var on :root)
  // governs both scroll-padding and JS-driven scrolls.
  const getHeaderStack = () => {
    const customProp = getComputedStyle(document.documentElement)
      .getPropertyValue('--header-stack')
      .trim();
    if (customProp) {
      const val = parseFloat(customProp);
      if (Number.isFinite(val) && val > 0) {
        if (customProp.endsWith('rem')) {
          const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
          return val * rootFontSize + 20;
        }
        return val + 20;
      }
    }
    const px = parseFloat(
      getComputedStyle(document.documentElement).scrollPaddingTop
    );
    return Number.isFinite(px) && px > 0 ? px : 148;
  };

  const scrollToHash = (rawHash, behavior = 'smooth', syncHistory = false) => {
    const normalized = normalizeHash(rawHash);
    const target = getHashTarget(normalized);
    if (!target) {
      return false;
    }

    const top =
      target.getBoundingClientRect().top +
      window.pageYOffset -
      getHeaderStack();

    if (syncHistory && normalized && window.location.hash !== normalized) {
      window.history.replaceState(null, '', normalized);
    }

    window.scrollTo({ top, behavior });
    return true;
  };

  // Flag to pause tracking during click-driven scrolls (TOC only)
  let isClickScrolling = false;

  // Helper: set a specific link as active.
  // Marks the matching href in EVERY TOC instance (desktop sidebar + mobile)
  // so the metro dots update on desktop even though activeLink may be a
  // mobile-side element found last by querySelectorAll.
  function setActiveLink(link) {
    tocLinks.forEach((l) => l.parentElement.classList.remove('is-active'));
    if (link) {
      const activeHref = link.getAttribute('href');
      tocLinks.forEach((l) => {
        if (l.getAttribute('href') === activeHref) {
          l.parentElement.classList.add('is-active');
        }
      });
      // Dynamically update the current label on the mobile floating pill
      const labelText = link.textContent.replace(/New$/i, '').trim();
      if (labelText) {
        document.querySelectorAll('.bfwt-brand-toc-floating .bfwt-brand-toc__current-label').forEach((el) => {
          el.textContent = labelText;
        });
      }
    }
  }

  // ============================================================
  // 1. Position check — using high-performance IntersectionObserver
  // ============================================================
  const sections = [];
  const linkMap = new Map();
  const activeSections = new Set();

  tocLinks.forEach((link) => {
    const href = link.getAttribute('href');
    if (href && href.startsWith('#')) {
      const section = getHashTarget(href);
      if (section) {
        sections.push(section);
        linkMap.set(section, link);
      }
    }
  });

  // Fallback / manual update: check viewport positions (for clicks or initial load)
  function setActiveByScroll() {
    const threshold = getHeaderStack() + 24;
    let activeLink = null;

    sections.forEach((section) => {
      const rect = section.getBoundingClientRect();
      if (rect.top <= threshold) {
        activeLink = linkMap.get(section);
      }
    });

    if (activeLink) {
      setActiveLink(activeLink);
    }
  }

  // ============================================================
  // 2. TOC Link Click and Active Observers
  // ============================================================
  if (tocLinks.length > 0) {
    // IntersectionObserver Options
    const observerOptions = {
      root: null,
      rootMargin: `-${getHeaderStack() + 24}px 0px -70% 0px`,
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          activeSections.add(entry.target);
        } else {
          activeSections.delete(entry.target);
        }
      });

      if (isClickScrolling) return;

      // Active section is the lowest intersecting section in DOM order
      let activeSection = null;
      sections.forEach((section) => {
        if (activeSections.has(section)) {
          activeSection = section;
        }
      });

      if (activeSection) {
        const link = linkMap.get(activeSection);
        if (link) {
          setActiveLink(link);
        }
      }
    }, observerOptions);

    sections.forEach((section) => observer.observe(section));

    // Initial check on load
    setActiveByScroll();

    tocLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        if (!href || !href.startsWith('#')) {
          return;
        }

        e.preventDefault();
        if (window.bfwt_trackEvent) {
          window.bfwt_trackEvent('toc_link_click', {
            section_id: href.substring(1),
            event_category: 'navigation',
          });
        }
        setActiveLink(link);
        isClickScrolling = true;

        scrollToHash(href, 'smooth', true);

        setTimeout(() => {
          isClickScrolling = false;
          setActiveByScroll();
        }, 1200);
      });
    });

    // On direct deep-links, browsers can miss final anchor position after late layout shifts.
    const ensureInitialHashAnchor = () => {
      if (!window.location.hash || !isTocHash(window.location.hash)) {
        return;
      }

      scrollToHash(window.location.hash, 'auto', true);
    };

    // Run twice to tolerate late-rendered blocks above the target section.
    setTimeout(ensureInitialHashAnchor, 120);
    window.addEventListener('load', () => {
      setTimeout(ensureInitialHashAnchor, 80);
    });

    window.addEventListener('hashchange', () => {
      if (!isTocHash(window.location.hash)) {
        return;
      }

      scrollToHash(window.location.hash, 'auto', true);
      setActiveByScroll();
    });
  }

  // ============================================================
  // 4.5. Header BFS quick-jump to score widget
  // ============================================================
  const bfsLink = document.querySelector(
    '.bfwt-brand-header__bfs-link[href="#budgetfitter-score"]'
  );
  if (bfsLink) {
    bfsLink.addEventListener('click', (e) => {
      e.preventDefault();
      if (window.bfwt_trackEvent) {
        window.bfwt_trackEvent('bfs_widget_jump', {
          section: 'budgetfitter-score',
          event_category: 'navigation',
        });
      }
      scrollToHash('#budgetfitter-score', 'smooth', true);
    });
  }

  // ============================================================
  // 5. Reply: scroll to form + show "Replying to [Name]" when replytocom in URL
  // ============================================================
  const replyContextEl = document.getElementById('bfwt-reply-context');
  const replyLabelEl = replyContextEl?.querySelector(
    '.bfwt-reply-context__label'
  );
  const cancelReplyEl = replyContextEl?.querySelector('[data-cancel-reply]');

  function updateReplyContext() {
    if (!replyContextEl || !replyLabelEl) return;
    const params = new URLSearchParams(window.location.search);
    const replyToComId = params.get('replytocom');
    if (replyToComId) {
      const commentEl = document.getElementById('comment-' + replyToComId);
      const nameEl = commentEl?.querySelector('.bfwt-comment-author-name');
      const name = nameEl
        ? (nameEl.textContent || nameEl.innerText).trim()
        : '';
      replyLabelEl.textContent = name
        ? 'Replying to ' + name
        : 'Reply to comment';
      replyContextEl.style.display = '';
      if (cancelReplyEl) {
        params.delete('replytocom');
        const qs = params.toString();
        const cleanUrl =
          window.location.pathname + (qs ? '?' + qs : '') + '#comments';
        cancelReplyEl.href = cleanUrl;
        cancelReplyEl.onclick = (e) => {
          e.preventDefault();
          history.replaceState(null, '', cleanUrl);
          replyContextEl.style.display = 'none';
        };
      }
    } else {
      replyContextEl.style.display = 'none';
    }
  }

  document.querySelectorAll('.bfwt-comment-reply a').forEach((a) => {
    a.addEventListener('click', () => {
      if (window.bfwt_trackEvent) {
        window.bfwt_trackEvent('comment_reply_start', {
          event_category: 'engagement',
        });
      }
      const respond = document.getElementById('respond');
      if (respond)
        {respond.scrollIntoView({ behavior: 'smooth', block: 'start' });}
      setTimeout(updateReplyContext, 400);
    });
  });

  if (
    window.location.hash === '#respond' ||
    window.location.search.includes('replytocom=')
  ) {
    updateReplyContext();
  }
  window.addEventListener('hashchange', updateReplyContext);

  // ============================================================
  // 6. Comment expand (long comments): gradient + Read more (Optimized Read/Write)
  // ============================================================
  function initCommentTruncation() {
    const commentContainers = document.querySelectorAll(
      '.bfwt-comments-list .comment-body, .bfwt-comments-widget-inner .comment-body, .bfwt-review-comments-widget .comment-body'
    );
    const TRUNCATE_HEIGHT = 120;
    const toTruncate = [];

    // Read Pass (Zero DOM mutations)
    commentContainers.forEach((body) => {
      const content = body.querySelector('.comment-content');
      if (!content || content.nextElementSibling?.classList?.contains('bfwt-comment-expand-wrap')) return;

      if (content.scrollHeight > TRUNCATE_HEIGHT) {
        toTruncate.push(content);
      }
    });

    // Write Pass (Batch DOM mutations)
    toTruncate.forEach((content) => {
      content.classList.add('bfwt-comment-body--truncated');

      const wrap = document.createElement('div');
      wrap.className = 'bfwt-comment-expand-wrap';

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className =
        'bfwt-comment-expand-btn tw-bg-transparent tw-border-none tw-text-accent tw-font-semibold tw-text-[0.8125rem] tw-cursor-pointer tw-py-[0.625rem] tw-px-0 tw-mt-[0.625rem] tw-inline-flex tw-items-center tw-gap-1.5';
      btn.setAttribute('aria-expanded', 'false');
      btn.innerHTML =
        'Read more <span class="material-symbols-rounded" aria-hidden="true">expand_more</span>';

      btn.addEventListener('click', () => {
        const expanded = content.classList.toggle('bfwt-comment-body--expanded');
        btn.setAttribute('aria-expanded', expanded);
        btn.innerHTML = expanded
          ? 'Show less <span class="material-symbols-rounded" aria-hidden="true">expand_more</span>'
          : 'Read more <span class="material-symbols-rounded" aria-hidden="true">expand_more</span>';
      });

      wrap.appendChild(btn);
      content.parentNode.insertBefore(wrap, content.nextSibling);
    });
  }

  initCommentTruncation();
  window.addEventListener('bfwt:comments-revealed', initCommentTruncation);

  // ── Floating Mobile TOC: Reveal on scroll ──
  const floatingToc = document.querySelector('.bfwt-brand-toc-floating');
  if (floatingToc) {
    const toggle = floatingToc.querySelector('.bfwt-brand-toc__toggle');
    const content = document.getElementById('toc-content');

    const handleScroll = () => {
      // Reveal floating pill once scrolled past the hero header
      if (window.scrollY > 180) {
        floatingToc.classList.remove('tw-opacity-0', 'tw-pointer-events-none', 'tw-translate-y-4');
        floatingToc.classList.add('tw-opacity-100', 'tw-translate-y-0');
      } else {
        floatingToc.classList.add('tw-opacity-0', 'tw-pointer-events-none', 'tw-translate-y-4');
        floatingToc.classList.remove('tw-opacity-100', 'tw-translate-y-0');
        // Auto-close if scrolled back to top
        if (toggle && content && toggle.getAttribute('aria-expanded') === 'true') {
          toggle.setAttribute('aria-expanded', 'false');
          content.hidden = true;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
  }
});
